class NourrieTabs extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    this.#build();

    document.addEventListener('shopify:block:select', this.#handleBlockSelect, {
      signal: this.#controller.signal,
    });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  get #panels() {
    return Array.from(this.querySelectorAll(':scope > .nourrie-tabs__panels > .nourrie-tabs__panel'));
  }

  get #triggers() {
    return Array.from(this.querySelectorAll(':scope > .nourrie-tabs__list > .nourrie-tabs__trigger'));
  }

  #build() {
    const list = this.querySelector(':scope > .nourrie-tabs__list');
    const panels = this.#panels;

    if (!list || panels.length === 0) return;

    const { signal } = this.#controller;
    list.innerHTML = '';

    panels.forEach((panel, index) => {
      const tabId = `nourrie-tab-${panel.id}`;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'nourrie-tabs__trigger';
      button.id = tabId;
      button.textContent = panel.dataset.tabTitle || '';
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-controls', panel.id);
      button.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      button.tabIndex = index === 0 ? 0 : -1;
      if (index === 0) button.classList.add('is-active');

      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tabId);
      panel.hidden = index !== 0;

      button.addEventListener('click', () => this.#activate(index), { signal });
      list.appendChild(button);
    });

    list.addEventListener('keydown', (event) => this.#handleKeyDown(event), { signal });
  }

  #activate(index) {
    const triggers = this.#triggers;
    const panels = this.#panels;

    triggers.forEach((btn, i) => {
      const active = i === index;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-selected', active ? 'true' : 'false');
      btn.tabIndex = active ? 0 : -1;
    });

    panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });
  }

  #handleKeyDown(event) {
    const triggers = this.#triggers;
    const currentIndex = triggers.findIndex((btn) => btn === document.activeElement);
    if (currentIndex === -1) return;

    let nextIndex = null;

    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % triggers.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + triggers.length) % triggers.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = triggers.length - 1;

    if (nextIndex !== null) {
      event.preventDefault();
      this.#activate(nextIndex);
      triggers[nextIndex].focus();
    }
  }

  #handleBlockSelect = (event) => {
    const panel = event.target.closest ? event.target.closest('.nourrie-tabs__panel') : null;
    if (!panel || !this.contains(panel)) return;

    const index = this.#panels.indexOf(panel);
    if (index !== -1) this.#activate(index);
  };
}

if (!customElements.get('nourrie-tabs')) {
  customElements.define('nourrie-tabs', NourrieTabs);
}
